import { Label } from "@/components/ui/label";
import { BetaBadge } from "@/components/ui/beta-badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ProviderId } from "@/lib/auth/orgSettings";

const META: Record<ProviderId, { label: string; description: string; beta?: boolean }> = {
  liveavatar: {
    label: "Heygen LiveAvatar",
    description: "Default. Voice conversation, transcripts and scoring.",
  },
  tavus: {
    label: "Tavus",
    description: "Adds learner camera and screen sharing, toggleable per scenario.",
  },
  did: {
    label: "D-ID",
    description: "Voice only. Adds a selectable scene background behind the avatar.",
    beta: true,
  },
  kaltura: {
    label: "Kaltura",
    description: "Voice only. Scene is baked into the chosen face; runs on our own conversation loop.",
    beta: true,
  },
};

export function ProviderPicker({
  value,
  available,
  onChange,
  disabled,
  helperText,
}: {
  value: ProviderId;
  available: ProviderId[];
  onChange: (v: ProviderId) => void;
  disabled?: boolean;
  helperText?: string;
}) {
  if (available.length <= 1) return null;
  const selected = META[value];
  return (
    <div className="space-y-1.5">
      <Label>Provider</Label>
      <Select
        value={value}
        onValueChange={(v) => onChange(v as ProviderId)}
        disabled={disabled}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select provider…">
            {selected && (
              <div className="flex flex-1 min-w-0 items-center justify-between gap-3">
                <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
                  <span className="truncate">{selected.label}</span>
                  {selected.beta && <BetaBadge />}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground whitespace-nowrap">
                  {selected.description}
                </span>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {available.map((p) => {
            const meta = META[p];
            return (
              <SelectItem key={p} value={p} textValue={meta.label}>
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <span className="truncate">{meta.label}</span>
                  {meta.beta && <BetaBadge />}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground whitespace-nowrap">
                  {meta.description}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      {helperText && (
        <p className="text-xs text-muted-foreground">{helperText}</p>
      )}
    </div>
  );
}
